/* Sign-in, against Supabase GoTrue, by hand.
 *
 * No client library. Three endpoints and a token refresh is not worth a
 * dependency, and for an app that spends its day offline the token lifecycle is
 * something to control rather than inherit:
 *
 *   · A field session must survive a whole day with no network. The library's
 *     default is to refresh eagerly and sign out on failure, which on a ridge
 *     means being logged out of an app whose entire promise is that it works
 *     with the radio off.
 *   · An expired access token must NEVER block a capture. Saving is local and
 *     unconditional; the token matters only when something is being sent, and
 *     by then there is a network to refresh on.
 *
 * So: keep the session, refresh opportunistically, and let a stale token be a
 * sync problem rather than an identity problem.
 */

export interface KilroySession {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms. */
  expiresAt: number;
  /** The row in `users` this account is, from app_metadata. */
  userId: string;
  email: string;
  name: string;
  role: string;
}

interface GoTrueSession {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: {
    email: string;
    app_metadata?: { kilroy_user_id?: string; kilroy_role?: string };
    user_metadata?: { name?: string };
  };
}

export class AuthError extends Error {
  constructor(
    message: string,
    /** True when the network was the problem rather than the credentials. */
    readonly offline = false,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

function toSession(g: GoTrueSession): KilroySession {
  const userId = g.user.app_metadata?.kilroy_user_id;
  if (!userId) {
    /* Every account is created with its users.id in app_metadata. Without it a
       session cannot say which person it is, and the app would be back to the
       slug-versus-uuid mismatch that made "assigned to you" match nothing. An
       account in that state is broken and should say so rather than sign in
       and quietly show an empty screen. */
    throw new AuthError('This account is not linked to a Kilroy user. Ask an admin to relink it.');
  }
  return {
    accessToken: g.access_token,
    refreshToken: g.refresh_token,
    expiresAt: Date.now() + g.expires_in * 1000,
    userId,
    email: g.user.email,
    name: g.user.user_metadata?.name ?? g.user.email,
    role: g.user.app_metadata?.kilroy_role ?? 'geology_team_member',
  };
}

export function createAuth(config: { url: string; anonKey: string }) {
  const base = `${config.url.replace(/\/$/, '')}/auth/v1`;
  const headers = { apikey: config.anonKey, 'content-type': 'application/json' };

  async function post(path: string, body: unknown): Promise<GoTrueSession> {
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
    } catch {
      throw new AuthError('No connection. Sign in once with signal and it lasts offline.', true);
    }
    if (!res.ok) {
      const detail = (await res.json().catch(() => ({}))) as { error_description?: string; msg?: string };
      const raw = detail.error_description ?? detail.msg ?? `HTTP ${res.status}`;
      // GoTrue says "Invalid login credentials", which is correct and useless.
      throw new AuthError(
        /invalid login/i.test(raw) ? 'That email and password do not match.' : raw,
      );
    }
    return (await res.json()) as GoTrueSession;
  }

  return {
    async signIn(email: string, password: string): Promise<KilroySession> {
      return toSession(await post('/token?grant_type=password', { email: email.trim(), password }));
    },

    async refresh(refreshToken: string): Promise<KilroySession> {
      return toSession(await post('/token?grant_type=refresh_token', { refresh_token: refreshToken }));
    },

    /** Sign-out is local. There is nothing to tell the server that matters. */
    signOut(): void {},
  };
}

/**
 * Is this token close enough to expiry to be worth refreshing?
 *
 * Five minutes of headroom, and only ever attempted when something is about to
 * be sent. Refreshing on a timer would wake the radio all day for no reason and
 * fail every time in a canyon, which is exactly when the battery matters most.
 */
export const needsRefresh = (session: KilroySession): boolean =>
  session.expiresAt - Date.now() < 5 * 60_000;
